import React, {useEffect, useMemo, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {fetchConceptsThunk, filtersConceptsEntitiesCollectionSelector, FormNameEnum} from 'filters-selections';
import {ItemWithPlaceholder} from 'filters-selections/selection/components';
import {Form} from 'forms';
import {IOption, IWithDisabled} from 'models';
import {useFormikContext} from 'formik';
import {IAddWebLinkFormBaseValues, IUploadFileFormBaseValues, IUploadScormFormBaseValues} from 'admin/resources/upload';
import {useIsCompetencySelected} from 'filters-selections/hooks';

interface IKnowledgeSkillsAndAbilitiesSelectProps extends IWithDisabled {
  infoText?: string;
}

type TKsaFormValue =
  | number
  | string
  | {id?: number | string; value?: number | string; name?: string; label?: string; displayText?: string};

const getKsaId = (entry: TKsaFormValue): number | string | null => {
  if (entry === null || entry === undefined || entry === '') {
    return null;
  }
  if (typeof entry === 'object') {
    // Prefer lookup id; fall back to autocomplete option value if still in form state.
    return entry.id ?? entry.value ?? null;
  }
  return entry;
};

const getKsaLabel = (entry: TKsaFormValue): string => {
  if (typeof entry === 'object') {
    return entry.name || entry.label || entry.displayText || '';
  }
  return '';
};

export function KnowledgeSkillsAndAbilitiesSelect({disabled, infoText}: IKnowledgeSkillsAndAbilitiesSelectProps) {
  const dispatch = useDispatch();
  const knowledgeSkillsAndAbilities = useSelector(filtersConceptsEntitiesCollectionSelector);
  const {hasCompetenciesSelected} = useIsCompetencySelected();
  const lastFetchedCompetencyKey = useRef<string>('');

  const {values, setFieldValue} = useFormikContext<
    IUploadFileFormBaseValues | IUploadScormFormBaseValues | IAddWebLinkFormBaseValues
  >();

  const competencyIds = (values.competencies ?? []).map(Number).filter((id) => !Number.isNaN(id));
  const competencyKey = competencyIds.join(',');

  // Always fetch concepts for the current competencies (do not skip when the store
  // still holds concepts from a previous competency selection).
  useEffect(() => {
    if (!competencyIds.length) {
      lastFetchedCompetencyKey.current = '';
      return;
    }

    if (lastFetchedCompetencyKey.current === competencyKey) {
      return;
    }

    lastFetchedCompetencyKey.current = competencyKey;
    dispatch(fetchConceptsThunk(competencyIds));
  }, [competencyIds, competencyKey, dispatch]);

  useEffect(() => {
    if (!competencyIds.length && (values.knowledgeSkillsAndAbilities?.length ?? 0) > 0) {
      setFieldValue(FormNameEnum.knowledgeSkillsAndAbilities, []);
    }
  }, [competencyIds.length, values.knowledgeSkillsAndAbilities, setFieldValue]);

  const options = useMemo(() => {
    const fromStore: IOption[] = knowledgeSkillsAndAbilities.map((knowledge) => ({
      value: knowledge.id,
      label: knowledge.name,
    }));

    const selectedEntries = (values.knowledgeSkillsAndAbilities ?? []) as TKsaFormValue[];
    const selectedStubs = selectedEntries
      .map((entry) => {
        const id = getKsaId(entry);
        if (id === null || id === undefined || id === '') {
          return null;
        }
        if (fromStore.some((option) => String(option.value) === String(id))) {
          return null;
        }
        const label = getKsaLabel(entry);
        return {
          value: typeof id === 'string' && !Number.isNaN(Number(id)) ? Number(id) : id,
          // Prefer API/display name; never fall back to a bare id when a name exists.
          label: label || String(id),
        };
      })
      .filter((option): option is IOption => !!option);

    return [...selectedStubs, ...fromStore];
  }, [knowledgeSkillsAndAbilities, values.knowledgeSkillsAndAbilities]);

  const shouldDisableAutocomplete = disabled || !competencyIds.length;
  const hasItems = hasCompetenciesSelected ? !!options.length || !!values.knowledgeSkillsAndAbilities?.length : true;

  return (
    <ItemWithPlaceholder label="KSAs" hasItems={hasItems} infoText={infoText}>
      <Form.SelectAutocomplete
        name={FormNameEnum.knowledgeSkillsAndAbilities}
        options={options}
        multiple
        disabled={shouldDisableAutocomplete}
      />
    </ItemWithPlaceholder>
  );
}
